import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const PRODUCT_NAME = 'SURGICAL INSTRUMENTS VISUAL ATLAS™'
export const CHECKOUT_URL = 'https://surgical-instruments-visual-atlas.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1074520198588474'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/iehMjwoBnq7z1R8CZaskzg/Diseno_sin_titulo-13.webp'

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/f4ApXobos48ftM3TxDQD72/Captura_de_pantalla_2026-10-04_a_la(s)_6.58.58_p._m..png',
    alt: 'Gynecologic instrument recognition — selected instrument families with profile, curvature, handle, working end and distinguishing features',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/7QJTnekeUTEVHQ8oSBK3eG/Captura_de_pantalla_2026-10-04_a_la(s)_6.58.42_p._m..png',
    alt: 'Tray challenge — can you name all 10? Numbered instrument tray with answer key and functional families',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/v1ycFpDg4sBQ9xVNC3Z9A3/Captura_de_pantalla_2026-10-04_a_la(s)_6.58.26_p._m..png',
    alt: 'Retractors — start with the structure: handheld versus self-retaining retractors with labeled blades, handles, prongs and frames',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/doRfN9BicUzFTg5U1RTkhf/Captura_de_pantalla_2026-10-04_a_la(s)_6.58.12_p._m..png',
    alt: 'Four clamps — four visual profiles: Mosquito, Kelly, Crile and Kocher/Ochsner-style jaw close-ups and serration clues',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/5Ek1DNFRvtt9S5kifF1vvi/Captura_de_pantalla_2026-10-04_a_la(s)_6.57.53_p._m..png',
    alt: 'How to read a surgical tray — six functional zones and a six-step visual identification method',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 SO MANY INSTRUMENTS. SO MANY SIMILAR DESIGNS.',
    body: 'Straight or curved? Toothed or smooth? Serrated or atraumatic? Handheld or self-retaining? Small differences in jaws, tips, blades, handles, teeth, and mechanisms can completely change the identification.',
  },
  {
    title: '⚠️ YOU RECOGNIZE THE INSTRUMENT—BUT CAN YOU NAME IT?',
    body: "You've seen it before. You may even know its general function. The challenge is identifying exactly what it is and recognizing the visual characteristics that separate it from similar instruments.",
  },
  {
    title: '📚 YOUR STUDY MATERIAL IS EVERYWHERE',
    body: "Textbooks, lecture notes, PDFs, flashcards, websites, videos, instrument lists and course materials. The information exists—but it's scattered when you're trying to study and compare instruments.",
  },
  {
    title: '😩 STOP MEMORIZING INSTRUMENTS AS RANDOM LISTS',
    body: 'Learning isolated names without understanding instrument families and visual differences makes recognition harder. Organize what you study by function, structure, working end and distinguishing characteristics.',
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'SURGICAL INSTRUMENT IDENTIFICATION & CLASSIFICATION™',
    body: 'Build the foundation for recognizing instrument families through handles, rings, ratchets, shafts, jaws, tips, blades, teeth, serrations, working ends and other distinguishing visual characteristics.',
  },
  {
    number: 2,
    title: 'CUTTING & DISSECTING INSTRUMENTS™',
    body: 'Recognize and compare scissors, scalpels, blades, dissectors and related instruments through shape, curvature, blade proportions, tip configuration and visual differences.',
  },
  {
    number: 3,
    title: 'GRASPING, HOLDING & CLAMPING INSTRUMENTS™',
    body: 'Differentiate tissue forceps, dressing forceps, Adson, DeBakey-style, Allis, Babcock, hemostats, Kelly, Crile, Kocher/Ochsner-style clamps and other commonly studied designs.',
  },
  {
    number: 4,
    title: 'RETRACTING, PROBING, DILATING & EXPOSURE INSTRUMENTS™',
    body: 'Identify handheld and self-retaining retractors, probes and dilators through blade shape, prongs, curvature, handles, frames, mechanisms and characteristic working ends.',
  },
  {
    number: 5,
    title: 'SUTURING, SUCTION & SPECIALIZED SURGICAL INSTRUMENTS™',
    body: 'Study needle holders, suturing-related instruments, suction instruments and selected specialty instrument families with focused visual comparisons and recognition references.',
  },
  {
    number: 6,
    title: 'SURGICAL SETS, TRAY RECOGNITION & PROCEDURE-BASED REVIEW™',
    body: 'Move beyond isolated instruments and practice recognition through educational tray layouts, grouped sets, labeled references, comparison exercises and active visual recall.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'SURGICAL INSTRUMENT QUICK RECOGNITION GUIDE™',
    body: 'Quickly review commonly studied instruments through functional families, identifying characteristics, working-end details, visual clues and concise comparison references.',
  },
  {
    number: 2,
    title: 'LOOK-ALIKE SURGICAL INSTRUMENTS COMPARISON GUIDE™',
    body: 'Focus specifically on instruments that are easy to confuse and learn which differences in jaws, tips, teeth, serrations, blades, proportions and mechanisms deserve your attention.',
  },
  {
    number: 3,
    title: 'SURGICAL TRAY & SET RECOGNITION WORKBOOK™',
    body: 'Practice identifying instruments inside representative educational tray layouts and grouped sets instead of always studying each instrument individually.',
  },
  {
    number: 4,
    title: 'SURGICAL INSTRUMENT ACTIVE RECALL & SELF-TEST PACK™',
    body: 'Turn passive studying into active practice with identification challenges, working-end recognition, family classification, look-alike comparisons and explained answers.',
  },
  {
    number: 5,
    title: 'SURGICAL INSTRUMENT STUDY & MEMORY SYSTEM™',
    body: 'Organize your study by functional family, distinguishing features, common look-alikes and structured review using reusable memory maps, comparison tools and study templates.',
  },
]

export const differentiators: Differentiator[] = [
  {
    icon: 'microscope',
    title: 'Practical visual references',
    body: 'Find the instrument family, visual characteristic, comparison or identification reference you need.',
  },
  {
    icon: 'search',
    title: 'Structured recognition system',
    body: 'Move from overall shape and functional family to working ends, distinguishing characteristics and specific instrument identification.',
  },
  {
    icon: 'zap',
    title: '6 dedicated study areas',
    body: 'Identification, cutting and dissecting, grasping and clamping, retracting, specialized instrumentation and surgical set recognition.',
  },
  {
    icon: 'gift',
    title: '5 practical study bonuses',
    body: 'Quick recognition, look-alike comparisons, tray practice, active recall and structured study tools.',
  },
  {
    icon: 'refresh',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to study, compare, review or reinforce surgical instrument recognition.',
  },
]

export const offerBullets = [
  '6 comprehensive visual modules',
  '5 strategic professional bonuses',
  'Surgical instrument identification & classification',
  'Cutting, dissecting, grasping & clamping recognition',
  'Retracting, suturing & suction instrument references',
  'Look-alike instrument comparisons',
  'Surgical set & tray recognition',
  'Visual recall and identification exercises',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Emily Carter, 24',
    role: 'Surgical Technology Student',
    location: 'Austin, United States',
    headline: 'I finally understand what I should be looking at.',
    quote:
      'Before, I was trying to memorize the entire instrument. Breaking it down by the working end, teeth, shape and mechanism makes the differences much easier to understand.',
  },
  {
    name: 'Hannah Whitfield, 29',
    role: 'Perioperative Learner',
    location: 'Birmingham, United Kingdom',
    headline: 'The comparisons are what I needed.',
    quote:
      'Several clamps and forceps looked almost identical to me. Seeing similar instruments side by side and knowing which details to check gives me a much clearer way to study them.',
  },
  {
    name: 'Olivia Bennett, 22',
    role: 'Nursing Student',
    location: 'Vancouver, Canada',
    headline: 'Much easier than studying random instrument lists.',
    quote:
      'Instead of trying to remember names one after another, I can organize instruments by family first and then focus on the characteristics that separate them.',
  },
  {
    name: 'Daniel Hughes, 27',
    role: 'Surgical Technology Student',
    location: 'Brisbane, Australia',
    headline: 'The tray exercises make the study more realistic.',
    quote:
      'Recognizing one instrument alone is one thing. Seeing several together and having to identify them makes me pay much more attention to the actual visual differences.',
  },
  {
    name: 'Rachel Thompson, 36',
    role: 'Healthcare Learner',
    location: 'Denver, United States',
    headline: 'A practical system I can keep reviewing.',
    quote:
      'I like having the visual references, comparisons and active-recall material organized together instead of constantly jumping between notes, websites and different resources.',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Surgical technology students, perioperative learners, nursing students, operating room trainees and healthcare professionals studying or reviewing surgical instrumentation.',
  },
  {
    q: 'What instruments does it cover?',
    a: 'The system covers representative instruments across cutting and dissecting, grasping and holding, clamping and occluding, retracting and exposing, suturing, suction, probing, dilating and selected specialized instrument families.',
  },
  {
    q: 'Does it include similar-looking instrument comparisons?',
    a: 'Yes. Visual differentiation is a central part of the system, with side-by-side comparisons and attention to working ends, teeth, serrations, tips, blades, curvature and mechanisms.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. SURGICAL INSTRUMENTS VISUAL ATLAS™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a visual educational and reference system for studying surgical instrumentation.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. The system focuses on visual identification, comparisons, functional classification, working-end recognition, tray exercises and practical study resources.',
  },
  {
    q: 'Does this replace institutional training or clinical protocols?',
    a: 'No. Institution-specific instruments, tray configurations, clinical procedures and requirements should always be learned and verified through the applicable institution, program, manufacturer information and qualified professional supervision.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — phone, tablet, laptop or desktop. Review it on your phone between classes, or print the comparison guides, tray exercises and self-tests you use most.',
  },
  {
    q: "What if it isn't right for me?",
    a: "You're covered by the 7-Day Money-Back Guarantee. If it isn't right for you, contact us within 7 days for a full refund according to the refund terms.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Sophie H.', city: 'London', country: 'United Kingdom' },
  { name: 'Megan T.', city: 'Toronto', country: 'Canada' },
  { name: 'Ashley R.', city: 'Houston', country: 'United States' },
  { name: 'Chloe M.', city: 'Melbourne', country: 'Australia' },
  { name: 'Liam P.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Brianna K.', city: 'Calgary', country: 'Canada' },
  { name: 'Jordan W.', city: 'Miami', country: 'United States' },
  { name: 'Ella S.', city: 'Perth', country: 'Australia' },
  { name: 'Grace D.', city: 'Edinburgh', country: 'United Kingdom' },
  { name: 'Nathan B.', city: 'Winnipeg', country: 'Canada' },
  { name: 'Kayla J.', city: 'Phoenix', country: 'United States' },
  { name: 'Ruby C.', city: 'Adelaide', country: 'Australia' },
]
